import type { IsoDateTime, Uuid } from '@/shared/types/domain'

/**
 * Las preguntas y las respuestas del brief de onboarding.
 *
 * **Las preguntas NO están escritas acá.** Vienen del backend
 * (`GET /onboarding/token/{token}`), que es su única fuente de verdad: la
 * plantilla sale del documento de metodología y el markdown que se archiva se
 * arma con las mismas definiciones. Una segunda copia en el front sería una
 * copia que se desfasa la primera vez que alguien reformula una pregunta.
 */

export type TipoDeCampo = 'texto' | 'texto_largo' | 'tabla'

export interface Pregunta {
  id: string
  label: string
  ayuda: string
  tipo: TipoDeCampo
  requerida: boolean
  /** Sólo en `tabla`: los encabezados. */
  columnas: string[]
  /** Cuántas filas se ofrecen de arranque en una tabla. */
  filas: number
}

export interface Seccion {
  id: string
  titulo: string
  preguntas: Pregunta[]
}

export interface BriefPublico {
  company_name: string
  project_name: string
  expires_at: IsoDateTime
  /** Ya respondido: se muestra el acuse, no el formulario otra vez. */
  submitted: boolean
  secciones: Seccion[]
}

/** Una respuesta: texto, o una grilla de celdas para las de tipo tabla. */
export type Respuesta = string | string[][]

export interface Contacto {
  name: string
  email: string
  role: string
  phone: string
}

export type EstadoDeInvitacion = 'pending' | 'submitted' | 'accepted' | 'revoked'

export interface Invitacion {
  id: Uuid
  company_id: Uuid
  project_id: Uuid
  status: EstadoDeInvitacion
  sent_to_email: string
  sent_to_name: string | null
  company_name: string
  project_name: string
  expires_at: IsoDateTime
  submitted_at: IsoDateTime | null
  accepted_at: IsoDateTime | null
  created_by_id: Uuid | null
  accepted_by_id: Uuid | null
  document_id: Uuid | null
  answers: Record<string, Respuesta> | null
  contact_name: string | null
  contact_email: string | null
  contact_role: string | null
  contact_phone: string | null
}

export interface InvitacionEmitida {
  invitation: Invitacion
  /**
   * El link completo. **Es la única vez que existe.**
   *
   * En la base queda su SHA-256, así que ni el servidor puede volver a
   * mostrarlo. La pantalla tiene que dejar eso claro antes de que alguien la
   * cierre sin copiarlo.
   */
  url: string
  /**
   * Si el mail con el link quedó encolado.
   *
   * `false` no es un error: puede ser que todavía no haya bandeja conectada.
   * Son dos instrucciones distintas para quien acaba de emitirlo —"ya se
   * mandó" o "copialo y mandalo vos"— y no da lo mismo cuál lee antes de
   * cerrar el cartel.
   */
  emailed: boolean
}

/** Las claves del catálogo para cada estado. El texto no vive acá. */
export const ESTADO_KEY: Record<EstadoDeInvitacion, string> = {
  pending: 'onboarding.status.pending',
  submitted: 'onboarding.status.submitted',
  accepted: 'onboarding.status.accepted',
  revoked: 'onboarding.status.revoked',
}
